import React, { memo, useState, useCallback, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { Modal, Button, Tooltip, Typography, Spin } from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getRoleList } from "redux/actions/getRole.action";
import { getFacilityByModule } from "redux/actions/getFacilityByModule.action";
import { getApiArrayDetails, getBase64URI } from "utils/MyMUtils";
import { getApi, postApi } from "utils/apiCall";
import {
  BRAND,
  STEPS,
  INITIAL_IDENTITY,
  INITIAL_LOGIN,
  INITIAL_COMPLIANCE,
  FIXED_MODULES,
  ALLOWED_MODULE_IDS,
  filterAllowedModules,
  validateName,
  validateMobile,
  validateUsername,
  validateEmail,
  StepIndicator,
  IdentityStep,
  RoleStep,
  FacilityStep,
  LoginStep,
  validateLoginPassword,
  ComplianceStep,
  AnimatedStep,
} from "./staffModalShared";

const { Text } = Typography;

const toMoment = (dateStr) => (dateStr ? moment(dateStr) : null);

const DOCUMENT_TYPE_MAP = {
  flu: "influenza",
  covid: "covid",
  stat: "statutory_doc",
  police: "police_check",
};

const formatDateForApi = (value) => {
  if (!value) return null;
  if (typeof value.format === "function") return value.format("YYYY-MM-DD");
  return null;
};

const buildDocumentMetadata = (key, document) => {
  switch (key) {
    case "flu":
    case "covid":
      return { vaccination_date: formatDateForApi(document.vaccinationDate), comment: "" };
    case "police":
      return {
        ref_number: document.referenceNumber.trim(),
        issue_date: formatDateForApi(document.issueDate),
        certificate_sited: Number(document.certificateSighted),
        are_there_offences: Number(document.offences),
      };
    case "stat":
    default:
      return {};
  }
};

const mapApiToFormState = (result) => {
  const doc = result.document || {};

  const fluDoc = doc.flu_vaccination;
  const covidDoc = doc.covid_vaccination;
  const statDoc = doc.statutory_declaration;
  const policeDoc = doc.police_check;

  const facilityList = Array.isArray(result.facilities) ? result.facilities : [];

  const facilities = facilityList.reduce((acc, f) => {
    if (f.facility_id != null) acc[f.facility_id] = true;
    return acc;
  }, {});

  const facilityModules = facilityList.reduce((acc, f) => {
    if (f.facility_id != null) {
      // Keep ALL modules from API response
      acc[f.facility_id] = (f.modules || []).reduce((mAcc, m) => {
        if (m.module_id != null) mAcc[m.module_id] = true;
        return mAcc;
      }, {});
    }
    return acc;
  }, {});

  const isDocActive = (status) =>
    status === "uploaded" || status === "expired" || status === "expiring_soon";

  const compliance = {
    flu: {
      file: isDocActive(fluDoc?.status) ? { name: "flu_vaccination", isExisting: true } : null,
      vaccinationDate: toMoment(fluDoc?.vaccination_date),
    },
    covid: {
      file: isDocActive(covidDoc?.status) ? { name: "covid_vaccination", isExisting: true } : null,
      vaccinationDate: toMoment(covidDoc?.vaccination_date),
    },
    stat: {
      file: isDocActive(statDoc?.status)
        ? { name: "statutory_declaration", isExisting: true, url: statDoc?.document_link || null }
        : null,
    },
    police: {
      file: isDocActive(policeDoc?.status)
        ? { name: "police_check", isExisting: true, url: policeDoc?.document_link || null }
        : null,
      referenceNumber: policeDoc?.ref_number || "",
      certificateSighted: policeDoc?.certificate_sighted != null
        ? policeDoc.certificate_sighted === true || policeDoc.certificate_sighted === "1" || policeDoc.certificate_sighted === 1
        : null,
      offences: policeDoc?.offences != null
        ? policeDoc.offences === true || policeDoc.offences === "1" || policeDoc.offences === 1
        : null,
      issueDate: toMoment(policeDoc?.issue_date),
    },
  };

  return {
    staffId: result.id,
    identity: {
      firstName: result.first_name || "",
      lastName: result.last_name || "",
      mobileNo: result.mobile || "",
      isAgencyStaff: Boolean(result.is_agency_staff),
    },
    roleId: result.role_id ?? null,
    facilities,
    facilityModules,
    login: {
      username: result.user_name || "",
      email: result.email || "",
      resetPassword: false,
      password: "",
    },
    compliance,
  };
};

/**
 * EditStaffModal
 *
 * Props:
 *   visible   {boolean}  — controls modal visibility
 *   onClose   {function} — called when the modal should close
 *   staffId   {number|string} — ID of the staff record to load and edit
 */
const EditStaffModal = ({ visible, onClose, staffId, onUpdated }) => {
  const dispatch = useDispatch();
  const roles = useSelector((state) => getApiArrayDetails(state.getRole.data));
  const rolesLoading = useSelector((state) => state.getRole.isLoading);
  const facilityListData = useSelector((state) =>
    getApiArrayDetails(state.getFacilityByModule.data)
  );
  const facilityListLoading = useSelector(
    (state) => state.getFacilityByModule.isLoading
  );

  const [currentStep, setCurrentStep] = useState(1);
  const [identity, setIdentity] = useState(INITIAL_IDENTITY);
  const [login, setLogin] = useState(INITIAL_LOGIN);
  const [compliance, setCompliance] = useState(INITIAL_COMPLIANCE);
  const [selectedRole, setSelectedRole] = useState(null);
  const [facilities, setFacilities] = useState({});
  const [facilityModules, setFacilityModules] = useState({});
  const [fetchLoading, setFetchLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [loadedStaffId, setLoadedStaffId] = useState(null);

  // Fetch staff details from API whenever the modal opens with a new staffId
  useEffect(() => {
    if (!visible || !staffId) return;
    if (staffId === loadedStaffId) return;

    let isMounted = true;

    const fetchStaff = async () => {
      setFetchLoading(true);
      const { error, result } = await getApi(`/api/staff/listing/${staffId}`);
      if (!isMounted) return;

      if (!error && result) {
        const data = result.result ?? result;
        const state = mapApiToFormState(data);
        setIdentity(state.identity);
        setSelectedRole(state.roleId);
        setFacilities(state.facilities);
        setFacilityModules(state.facilityModules);
        setLogin(state.login);
        setCompliance(state.compliance);
        setLoadedStaffId(staffId);
      }

      setFetchLoading(false);
    };

    fetchStaff();

    return () => {
      isMounted = false;
    };
  }, [visible, staffId, loadedStaffId]);

  const allModules = useMemo(() => {
    if (!facilityListData) return [];
    const moduleMap = new Map();
    facilityListData.forEach((f) => {
      filterAllowedModules(f.modules || []).forEach((m) => {
        if (!moduleMap.has(m.module_id)) moduleMap.set(m.module_id, m);
      });
    });
    return Array.from(moduleMap.values());
  }, [facilityListData]);

  // Fetch roles when modal opens
  useEffect(() => {
    if (visible) {
      dispatch(getRoleList());
    }
  }, [visible, dispatch]);

  // Fetch facilities whenever the selected role changes
  useEffect(() => {
    if (selectedRole !== null) {
      dispatch(getFacilityByModule(selectedRole));
    }
  }, [selectedRole, dispatch]);

  // When the user manually picks a new role (not during API load), clear stale facility
  // selections so the FacilityStep starts fresh for the new role's facility list.
  const handleRoleSelect = useCallback(
    (roleId) => {
      if (roleId !== selectedRole) {
        setFacilities({});
        setFacilityModules({});
      }
      setSelectedRole(roleId);
    },
    [selectedRole]
  );

  const handleIdentityChange = useCallback((field, value) => {
    setIdentity((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleLoginChange = useCallback((field, value) => {
    setLogin((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleFileChange = useCallback((key, file) => {
    setCompliance((prev) => ({ ...prev, [key]: { ...prev[key], file } }));
  }, []);

  const handleComplianceFieldChange = useCallback((itemKey, field, value) => {
    setCompliance((prev) => ({
      ...prev,
      [itemKey]: { ...prev[itemKey], [field]: value },
    }));
  }, []);

  const toggleFacility = useCallback(
    (facilityId) => {
      setFacilities((prev) => {
        const nowSelected = !prev[facilityId];
        if (nowSelected && facilityListData) {
          const facility = facilityListData.find(
            (f) => f.facility_id === facilityId
          );
          if (facility) {
            setFacilityModules((mPrev) => ({
              ...mPrev,
              // Enable ALL modules from API when facility is selected
              [facilityId]: (facility.modules || []).reduce(
                (acc, m) => ({ ...acc, [m.module_id]: true }),
                {}
              ),
            }));
          }
        }
        return { ...prev, [facilityId]: nowSelected };
      });
    },
    [facilityListData]
  );

  const toggleModule = useCallback((facilityKey, modKey) => {
    setFacilityModules((prev) => {
      const updatedMods = {
        ...prev[facilityKey],
        [modKey]: !prev[facilityKey][modKey],
      };
      const anyModuleOn = Object.values(updatedMods).some(Boolean);
      if (!anyModuleOn) {
        setFacilities((fPrev) => ({ ...fPrev, [facilityKey]: false }));
      }
      return { ...prev, [facilityKey]: updatedMods };
    });
  }, []);

  const handleClose = useCallback(() => {
    setCurrentStep(1);
    setIdentity(INITIAL_IDENTITY);
    setLogin(INITIAL_LOGIN);
    setCompliance(INITIAL_COMPLIANCE);
    setSelectedRole(null);
    setFacilities({});
    setFacilityModules({});
    setLoadedStaffId(null);
    onClose();
  }, [onClose]);

  const isManagerRole = useMemo(
    () =>
      Boolean(
        roles &&
          selectedRole !== null &&
          (roles.find((r) => r.role_id === selectedRole)?.role_name || "")
            .toLowerCase() === "manager"
      ),
    [roles, selectedRole]
  );

  const isSupportStaffRole = useMemo(
    () =>
      Boolean(
        roles &&
          selectedRole !== null &&
          (roles.find((r) => r.role_id === selectedRole)?.role_name || "")
            .toLowerCase() === "support staff"
      ),
    [roles, selectedRole]
  );

  const requiresEmail = isManagerRole || isSupportStaffRole;

  const buildDocumentsPayload = useCallback(async () => {
    const documents = await Promise.all(
      Object.entries(compliance).map(async ([key, document]) => {
        if (!document.file || document.file.isExisting) return null;
        const { base64String } = await getBase64URI(document.file);
        return {
          document_type: DOCUMENT_TYPE_MAP[key],
          filename: document.file.name,
          file: base64String,
          metadata: buildDocumentMetadata(key, document),
        };
      })
    );
    return documents.filter(Boolean);
  }, [compliance]);

  const handleSave = useCallback(async () => {
    if (!staffId) return;
    setSaveLoading(true);
    try {
      const facilityPayload = Object.entries(facilities).reduce((acc, [facilityId, isSelected]) => {
        if (!isSelected) return acc;
        const modules = Object.entries(facilityModules[facilityId] || {})
          .filter(([, enabled]) => enabled)
          .map(([moduleId]) => Number(moduleId));
        if (!modules.length) return acc;
        acc.push({ facility_id: Number(facilityId), modules });
        return acc;
      }, []);

      const payload = {
        user_id: staffId,
        first_name: identity.firstName.trim(),
        last_name: identity.lastName.trim(),
        mobile: identity.mobileNo.replace(/\s/g, ""),
        is_agency_staff: Number(identity.isAgencyStaff),
        email: requiresEmail ? login.email.trim() : "",
        user_name: login.username.trim(),
        ...(login.resetPassword && login.password ? { password: login.password.trim() } : {}),
        role_id: selectedRole,
        facility: facilityPayload,
        documents: await buildDocumentsPayload(),
      };

      const { error, result } = await postApi("/api/staff/create-assign-upload", payload);

      if (error || result?.success === false) {
        toast.error(result?.message || "Unable to update the staff profile.");
        return;
      }

      const staffFullName = `${identity.firstName.trim()} ${identity.lastName.trim()}`.trim();
      toast.success(`${staffFullName} was updated successfully.`);
      if (typeof onUpdated === "function") onUpdated();
      handleClose();
    } catch (err) {
      toast.error(err?.message || "Unable to update the staff profile.");
    } finally {
      setSaveLoading(false);
    }
  }, [
    staffId,
    identity,
    selectedRole,
    facilities,
    facilityModules,
    login,
    isManagerRole,
    buildDocumentsPayload,
    handleClose,
    onUpdated,
  ]);

  const goNext = useCallback(
    () => setCurrentStep((s) => Math.min(s + 1, STEPS.length)),
    []
  );
  const goBack = useCallback(
    () => setCurrentStep((s) => Math.max(s - 1, 1)),
    []
  );

  const isLastStep = currentStep === STEPS.length;

  const hasValidFacility = Object.entries(facilities).some(
    ([facilityId, isSelected]) =>
      isSelected &&
      Object.values(facilityModules[facilityId] || {}).some(Boolean)
  );

  const hasValidCompliance = (() => {
    if (compliance.flu.file !== null && compliance.flu.vaccinationDate === null)
      return false;
    if (
      compliance.covid.file !== null &&
      compliance.covid.vaccinationDate === null
    )
      return false;
    if (compliance.police.file !== null) {
      const d = compliance.police;
      if (
        !d.referenceNumber.trim() ||
        d.certificateSighted === null ||
        d.offences === null ||
        d.issueDate === null
      )
        return false;
    }
    return true;
  })();

  const hasValidIdentity =
    !validateName(identity.firstName) &&
    !validateName(identity.lastName) &&
    !validateMobile(identity.mobileNo);

  // In edit mode username is required; password only required when toggle is on
  const hasValidLogin =
    !validateUsername(login.username) &&
    (!login.resetPassword || !validateLoginPassword(login)) &&
    (!requiresEmail || !validateEmail(login.email));

  const canProceed =
    !(currentStep === 1 && !hasValidIdentity) &&
    !(currentStep === 2 && selectedRole === null) &&
    !(currentStep === 3 && !hasValidFacility) &&
    !(currentStep === 4 && !hasValidLogin);

  const staffName =
    identity.firstName || identity.lastName
      ? `${identity.firstName} ${identity.lastName}`.trim()
      : "Staff Member";

  const stepContent = (() => {
    switch (currentStep) {
      case 1:
        return (
          <IdentityStep identity={identity} onChange={handleIdentityChange} />
        );
      case 2:
        return (
          <RoleStep
            selectedRole={selectedRole}
            onSelect={handleRoleSelect}
            roles={roles || []}
            isLoading={rolesLoading}
          />
        );
      case 3:
        return (
          <FacilityStep
            facilityList={facilityListData || []}
            allModules={allModules}
            isLoading={facilityListLoading}
            facilities={facilities}
            onToggleFacility={toggleFacility}
            facilityModules={facilityModules}
            onToggleModule={toggleModule}
          />
        );
      case 4:
        return (
          <LoginStep
            login={login}
            onChange={handleLoginChange}
            mode="edit"
            isManager={isManagerRole}
            isSupportStaff={isSupportStaffRole}
          />
        );
      case 5:
        return (
          <ComplianceStep
            compliance={compliance}
            onFileChange={handleFileChange}
            onFieldChange={handleComplianceFieldChange}
          />
        );
      default:
        return null;
    }
  })();

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      width={750}
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#101828" }}>
            Edit Staff
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 400,
              color: "#5B6472",
              marginTop: 2,
            }}
          >
            Update staff record, role, facility access, login or compliance.
          </div>
        </div>
      }
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text style={{ fontSize: 12, color: "#5B6472" }}>
            Step {currentStep} of {STEPS.length} —{" "}
            {STEPS[currentStep - 1].label}
          </Text>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={handleClose}>Cancel</Button>
            {currentStep > 1 && (
              <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
                Back
              </Button>
            )}
            {!isLastStep ? (
              <Tooltip
                title={
                  currentStep === 1 && !hasValidIdentity
                    ? "First name, last name, and mobile number are required to continue."
                    : currentStep === 2 && selectedRole === null
                    ? "Please select a role to continue."
                    : currentStep === 3 && !hasValidFacility
                    ? "Please select at least one facility with a module to continue."
                    : currentStep === 4 && !hasValidLogin
                    ? "Please fill in all required login fields correctly to continue."
                    : ""
                }
              >
                <Button
                  type="primary"
                  style={{ background: BRAND, borderColor: BRAND }}
                  icon={<ArrowRightOutlined />}
                  onClick={goNext}
                  disabled={!canProceed}
                >
                  Next
                </Button>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  !hasValidCompliance
                    ? "Complete or remove the uploaded compliance documents to save."
                    : ""
                }
              >
                <Button
                  type="primary"
                  style={{ background: BRAND, borderColor: BRAND }}
                  onClick={handleSave}
                  icon={<SaveOutlined />}
                  disabled={!hasValidCompliance}
                  loading={saveLoading}
                >
                  Save Changes
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      }
      bodyStyle={{ padding: "16px 20px", minHeight: 280 }}
      destroyOnClose
    >
      <Spin spinning={fetchLoading}>
        {/* Staff name banner */}
        <div
          style={{
            background: "#e8f0f9",
            borderRadius: 6,
            padding: "10px 16px",
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text style={{ fontSize: 15, fontWeight: 700, color: BRAND }}>
            {staffName}
          </Text>
          <Text style={{ fontSize: 12, color: "#5B6472" }}>Edit</Text>
        </div>

        <StepIndicator currentStep={currentStep} />

        <AnimatedStep stepKey={currentStep}>{stepContent}</AnimatedStep>
      </Spin>
    </Modal>
  );
};

export default memo(EditStaffModal);
